import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { useDateFormat } from '../../shared/DateFormatContext';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { CenteredLoader } from '../../components/LoadingSpinner';
import StatusBadge from '../../components/ui/StatusBadge';

interface ActivityUser {
  profile?: {
    first_name?: string;
    last_name?: string;
    photo?: string | null;
  };
}

interface ActivityEntry {
  history_date: string;
  history_type: '+' | '-' | '~' | string;
  history_user_id: number | null;
  history_user: ActivityUser | null;
  name: string;
  type: string;
  model: string;
  app_label: string;
  pk: number | string | null;
  extra?: {
    project_pk?: number | string;
    contract_pk?: number | string;
    rental_pk?: number | string;
    stock_pk?: number | string;
    item_pk?: number | string;
  };
}

interface ActivityPage {
  results: ActivityEntry[];
  next: number | null;
}

const PAGE_SIZE = 30;

const getActivityLink = (activity: ActivityEntry): string | null => {
  const { pk, model, app_label, extra = {} } = activity;
  if (extra.project_pk) return `/projects/detail/${extra.project_pk}`;
  if (extra.contract_pk) return `/contracts/contract/${extra.contract_pk}`;
  if (extra.rental_pk) return `/rentals/rental/${extra.rental_pk}`;
  if (extra.stock_pk && model !== 'stock') return `/warehouses/stock/${extra.stock_pk}`;

  switch (model) {
    case 'project': return `/projects/detail/${pk}`;
    case 'projectoffer': return extra.project_pk ? `/projects/detail/${extra.project_pk}/offers` : null;
    case 'contract': return `/contracts/contract/${pk}`;
    case 'contractoffer': return extra.contract_pk ? `/contracts/contract/${extra.contract_pk}` : null;
    case 'rental': return `/rentals/rental/${pk}`;
    case 'customer': return `/customers/customer/${pk}`;
    case 'stock': return `/warehouses/stock/${pk}`;
    case 'item': return extra.stock_pk ? `/warehouses/stock/${extra.stock_pk}` : '/warehouses/products';
    case 'product': return `/warehouses/products/${pk}`;
    case 'productcategory': return '/warehouses/products/categories';
    case 'producttype': return '/warehouses/products/types';
    case 'vehicle': return `/transport/${pk}`;
    case 'crewmember': return `/crew/${pk}`;
    default:
      break;
  }

  const appLinks: Record<string, string> = {
    accounts: '/accounts/profile',
    companies: '/settings/company',
    contracts: '/contracts',
    crew_members: '/crew',
    customers: '/customers',
    invoices: '/invoices',
    projects: '/projects',
    rentals: '/rentals',
    settings: '/settings',
    subscriptions: '/settings',
    support: '/support',
    transport: '/transport',
    user_preferences: '/accounts/profile',
    warehouses: '/warehouses/products',
  };
  return appLinks[app_label] ?? null;
};

const getBadgeColor = (model: string): string => ({
  project: '#206bc4', projectoffer: '#4263eb', contract: '#1098ad', contractoffer: '#4299e1',
  rental: '#0ca678', invoice: '#f59f00', customer: '#f76707', stock: '#74b816',
  item: '#2f9e44', itemtype: '#7048e8', itemcategory: '#d6336c', product: '#206bc4',
  producttype: '#4263eb', productcategory: '#1098ad', vehicle: '#e03131', crewmember: '#6c7a91',
}[model] || '#6c7a91');

export default function RecentActivity() {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const search = searchInput.trim();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<ActivityPage>({
    queryKey: ['dashboard', 'activities', search, dateFrom, dateTo],
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ page: String(pageParam), page_size: String(PAGE_SIZE) });
      if (search) params.set('search', search);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      return api.getRaw(`dashboard/api/dashboard/activities/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
  });

  const activities = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [search, dateFrom, dateTo]);

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container || !hasNextPage || isFetchingNextPage) return;
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 150) {
      fetchNextPage();
    }
  };

  const renderAction = (historyType: ActivityEntry['history_type']) => {
    const labels: Record<string, [string, string]> = {
      '+': [t('dashboard.added'), '#2fb344'],
      '~': [t('dashboard.modified'), '#4299e1'],
      '-': [t('dashboard.removed'), '#d63939'],
    };
    const [label, color] = labels[historyType] || [t('dashboard.otherAction'), '#6c7a91'];
    return <StatusBadge color={color} label={label} />;
  };

  return (
    <PageShell
      pretitle={t('dashboard.overview')}
      title={t('dashboard.recentActivity.title')}
      description={t('dashboard.recentActivity.subtitle')}
      actions={<Link to="/dashboard/" className="btn btn-outline-secondary"><i className="ti ti-arrow-left me-2" />{t('common.back')}</Link>}
    >
      <PageSection bodyClassName="p-0">
        <div className="p-3 border-bottom">
          <div className="row g-2">
            <div className="col-12 col-md">
              <div className="input-icon">
                <input
                  className="form-control"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder={t('dashboard.recentActivity.searchPlaceholder')}
                />
                <span className="input-icon-addon"><i className="ti ti-search" /></span>
              </div>
            </div>
            <div className="col-6 col-md-auto">
              <input className="form-control" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} aria-label={t('dashboard.recentActivity.dateFrom')} />
            </div>
            <div className="col-6 col-md-auto">
              <input className="form-control" type="date" value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} aria-label={t('dashboard.recentActivity.dateTo')} />
            </div>
          </div>
        </div>
        {isLoading ? <CenteredLoader /> : activities.length === 0 ? (
          <EmptyState icon={<i className="ti ti-history app-empty-state__icon-glyph" />} title={t('dashboard.recentActivity.emptyTitle')} message={t('dashboard.recentActivity.emptyMessage')} />
        ) : (
          <div ref={scrollRef} onScroll={handleScroll} className="overflow-auto" style={{ maxHeight: 680 }}>
            <Table hover className="table-vcenter app-table mb-0 align-middle">
              <thead className="sticky-top"><tr><th>{t('dashboard.recentActivity.user')}</th><th>{t('dashboard.recentActivity.action')}</th><th>{t('dashboard.recentActivity.type')}</th><th>{t('dashboard.recentActivity.item')}</th><th>{t('dashboard.recentActivity.dateAndTime')}</th></tr></thead>
              <tbody>{activities.map((activity, index) => {
                const link = getActivityLink(activity);
                const profile = activity.history_user?.profile;
                const initials = `${profile?.first_name?.slice(0, 1) || ''}${profile?.last_name?.slice(0, 1) || ''}`;
                return <tr key={`${activity.model}-${activity.pk}-${activity.history_date}-${index}`}>
                  <td className="w-1">{activity.history_user_id ? <Link to={`/accounts/profile/${activity.history_user_id}`}><span className="avatar avatar-sm" style={profile?.photo ? { backgroundImage: `url(${profile.photo.replace(/^https?:/, window.location.protocol)})` } : undefined}>{profile?.photo ? null : initials}</span></Link> : <span className="avatar avatar-sm bg-secondary-lt text-secondary">?</span>}</td>
                  <td>{renderAction(activity.history_type)}</td>
                  <td><StatusBadge color={getBadgeColor(activity.model)} label={activity.type} /></td>
                  <td className="td-truncate"><div className="text-truncate fw-medium">{link ? <Link to={link}>{activity.name}</Link> : activity.name}</div></td>
                  <td className="text-nowrap text-secondary">{formatDateTime(activity.history_date)}</td>
                </tr>;
              })}</tbody>
            </Table>
            {isFetchingNextPage && <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">{t('common.loading')}</span></div></div>}
          </div>
        )}
      </PageSection>
    </PageShell>
  );
}
